import React, { useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useEnrollmentAccess } from 'hooks/access/useEnrollmentAccess';
import { useLessonComments } from 'hooks/lesson/useLessonComments';
import { useCourseId } from 'hooks/course/useCourseId';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import Toast from 'components/Toas/Toast';
import LessonCommentForm from 'components/LessonComments/LessonCommentForm';
import LessonCommentItem from 'components/LessonComments/LessonCommentItem';

interface LessonCommentsProps {
  lessonId: string;
}

const LessonComments: React.FC<LessonCommentsProps> = ({ lessonId }) => {
  const { user } = useAuth();
  const courseId = useCourseId();
  const { canAccessLessons } = useEnrollmentAccess(courseId ?? '');
  const { comments, loading, submitting, createComment, editComment, deleteComment } = useLessonComments(
    lessonId,
    courseId
  );

  const [toastMessage, setToastMessage] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [savingCommentId, setSavingCommentId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const showError = (err: unknown, fallback: string) => {
    setToastMessage(err instanceof Error ? err.message : fallback);
  };

  const handleCreate = async (content: string) => {
    try {
      await createComment(content);
    } catch (err) {
      showError(err, 'Erro ao criar comentário');
      throw err;
    }
  };

  const handleSaveEdit = async (commentId: string, content: string) => {
    setSavingCommentId(commentId);
    try {
      await editComment(commentId, content);
      setEditingCommentId(null);
    } catch (err) {
      showError(err, 'Erro ao editar comentário');
    } finally {
      setSavingCommentId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) {
      return;
    }

    setDeletingCommentId(confirmDeleteId);
    try {
      await deleteComment(confirmDeleteId);
    } catch (err) {
      showError(err, 'Erro ao excluir comentário');
    } finally {
      setDeletingCommentId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mt-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Comentários</h2>

      {canAccessLessons && <LessonCommentForm submitting={submitting} onSubmit={handleCreate} />}

      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="flex justify-center items-center py-6 text-gray-500">
            <svg className="animate-spin h-6 w-6 text-terracota" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          </div>
        ) : comments.length === 0 ? (
          <p className="text-gray-600 text-sm">Nenhum comentário ainda. Seja o primeiro a comentar.</p>
        ) : (
          comments.map((comment) => (
            <LessonCommentItem
              key={comment.id}
              comment={comment}
              isAuthor={user?.id === comment.author.id}
              canAccessLessons={canAccessLessons}
              isEditing={editingCommentId === comment.id}
              isSaving={savingCommentId === comment.id}
              isDeleting={deletingCommentId === comment.id}
              onStartEdit={() => setEditingCommentId(comment.id)}
              onCancelEdit={() => setEditingCommentId(null)}
              onSaveEdit={(content) => handleSaveEdit(comment.id, content)}
              onRequestDelete={() => setConfirmDeleteId(comment.id)}
            />
          ))
        )}
      </div>

      <ConfirmModal
        title="Excluir comentário"
        isOpen={!!confirmDeleteId}
        message="Tem certeza que deseja excluir este comentário? Essa ação não pode ser desfeita."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
        confirmText="Sim, excluir"
        cancelText="Cancelar"
        isLoading={!!deletingCommentId}
      />

      <Toast message={toastMessage} type="error" onClose={() => setToastMessage('')} />
    </div>
  );
};

export default LessonComments;
